/**
 * Currency formatting utilities (NFR: Currency handling)
 */

/**
 * Formats a number as USD currency string.
 * Uses locale-aware formatting.
 */
export function formatCurrency(value: number | undefined, currency = 'USD'): string {
    if (value === undefined || value === null) return '';

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    } catch {
        return `$${value.toLocaleString()}`;
    }
}

/**
 * Parses a currency string back to a number.
 * Strips currency symbols, commas, and whitespace.
 */
export function parseCurrencyInput(input: string): number | undefined {
    if (!input) return undefined;
    const cleaned = input.replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? undefined : parsed;
}

export function sanitizeCurrencyInput(input: string): string {
    const cleaned = input.replace(/[^0-9.,$<>+-]/g, '');
    const prefix = cleaned.startsWith('<') ? '<' : cleaned.startsWith('>') ? '>' : '';
    const suffix = cleaned.endsWith('+') ? '+' : '';
    const value = cleaned.replace(/[$<>+]/g, '').replace(/,/g, '');
    if (!/\d/.test(value)) return '';
    return `${prefix}$${value}${suffix}`;
}

export function sanitizeWholeNumberInput(input: string): string {
    return input.replace(/\D/g, '');
}

export function isValidCurrencyInput(input: string): boolean {
    if (input === '') return true;
    if (!/^\$?\d[\d,]*(?:\.\d+)?$/.test(input.trim())) return false;
    const value = parseCurrencyInput(input);
    return value != null && value >= 0;
}

export function isValidWholeNumberInput(input: string): boolean {
    return input === '' || (/^\d+$/.test(input.trim()) && Number(input.trim()) >= 0);
}

/**
 * Formats a number with comma separators for display.
 */
export function formatNumber(value: number | undefined): string {
    if (value === undefined || value === null) return '';
    return value.toLocaleString('en-US');
}
